import { prop } from 'ramda';
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useDeviceSelector } from '@core/js/store/hooks';
import { Image } from '@core/js/components/common/Image';
import style from '../Game.scss';
import { useTranslations } from '@core/js/hooks/i18n';

import Hls from 'hls.js';

type LiveStreamingVideoProps = {
    asset: LiveStreamingVideoPropsAsset
    gameName: string,
    imageUrl: string,
    isUnderMaintenance?: boolean
}

type LiveStreamingVideoPropsAsset = {
    name: string,
    url: string
}

const LiveStreamingVideo = ({ asset, imageUrl, gameName, isUnderMaintenance }: LiveStreamingVideoProps) => {
    const [isVideoAvailable, setIsVideoAvailable] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const hlsRef = useRef<Hls | null>(null);
    const observerRef = useRef<IntersectionObserver | null>(null);
    const isMobile = useDeviceSelector(prop('isMobile'));
    const t = useTranslations('playtech.mobile.games-info.');
    const handleVideoError = (cleanup: boolean | ErrorEvent = false): void => {
        if (cleanup) {
            hlsRef.current?.destroy();
        }
        setIsLoading(false);
        setIsVideoAvailable(false);
    };

    const handleVideoLoaded = (start: boolean | Event = false): void => {
        const { current: video } = videoRef;

        if (start && video) {
            video.muted = true;
            video?.play();
        }
        setIsLoading(false);
        setIsVideoAvailable(true);
    };

    const observerHandler = useCallback(([observedVideo]) => {
        const { current: video } = videoRef;
        const { current: hls } = hlsRef;

        if (!observedVideo) {
            console.warn('No observed video');
            return;
        }

        if (observedVideo.isIntersecting) {
            if (video?.paused && !video?.src) {
                hls?.on(Hls.Events.ERROR, (event, data) => {
                    if (data.fatal) {
                        handleVideoError(true);
                    }
                });
                hls?.on(Hls.Events.MANIFEST_PARSED, () => handleVideoLoaded(true));
                hls?.loadSource(asset.url);
                hls?.attachMedia(video);

                return;
            }

            hls?.startLoad(-1);
            video?.play();
        } else if (!video?.paused && video?.src) {
            hls?.stopLoad();
        }
    }, [asset]);

    useEffect(() => {
        if (!asset || !videoRef.current || isUnderMaintenance) {
            setIsLoading(false);
            return;
        }

        const { current: video } = videoRef;
        if (!Hls.isSupported() && video?.canPlayType('application/vnd.apple.mpegurl')) {
            video.addEventListener('error', handleVideoError);
            video.addEventListener('play', handleVideoLoaded);
            video.src = asset.url;
            console.warn('Hls is not supported');
            return () => {
                video.removeEventListener('error', handleVideoError);
                video.removeEventListener('play', handleVideoLoaded);
            };
        }
        hlsRef.current = new Hls({ liveSyncDuration: 3 });
        observerRef.current = new IntersectionObserver(observerHandler, { threshold: 0.1, root: null });
        observerRef.current?.observe(video);

        return () => {
            observerRef.current?.disconnect();
            hlsRef.current?.destroy();
        };
    }, [isUnderMaintenance, asset, observerHandler]);

    const maintenanceElement = (
        <>
            <div className={style.maintenanceContainer}>
                <div className={style.maintenanceIconContainer}>
                    <div className={style.maintenanceIcon} />
                    <div className={`${style.maintenanceIcon} ${style.maintenanceIconSatelite}`} />
                </div>
            </div>
            <Image alt={gameName} src={imageUrl} className={style.image} />
        </>
    );

    if (isUnderMaintenance) {
        return (
            <>
                {maintenanceElement}
                <div className={style.liveGameName}>{t('maintenance')}</div>
            </>
        );
    }

    return (
        <>
            <video
                ref={videoRef}
                className={`${style.image} ${style.video}`}
                autoPlay={isMobile}
                loop={true}
                muted={true}
                playsInline={true}>
            </video>
            {!isVideoAvailable && !isLoading && maintenanceElement}
            {!isLoading && asset && (
                <div className={style.liveGameName}>{isVideoAvailable ? gameName : t('maintenance')}</div>
            )}
        </>
    );
};

export default LiveStreamingVideo;
